import { type Ref } from 'vue'
import { useState } from '#app'
import type { ModuleOptions } from 'nuxt/schema'
import type { RefreshHandler } from '@sidebase/nuxt-auth'
import type { CommonUseAuthReturn } from '@sidebase/nuxt-auth/dist/runtime/types.d'
import { getMainRole } from '~/utils/other'

export const useCurrentIoe = (): {
    currentIoe: Ref<unknown>;
    setCurrentIoe: (ioe: unknown) => void;
} => {
  const currentIoe = useState<unknown>('currentIoe', () => undefined)

  const setCurrentIoe = (ioe: unknown): void => {
    currentIoe.value = ioe
  }

  return {
    currentIoe,
    setCurrentIoe
  }
}

export const useIoeConfig = (): {
  ioeConfig: Ref<unknown>;
  setIoeConfig: (config: unknown) => void;
} => {
  const ioeConfig = useState<unknown>('ioeConfig', () => undefined)

  const setIoeConfig = (config: unknown): void => {
    ioeConfig.value = config
  }

  return {
    ioeConfig,
    setIoeConfig
  }
}

export const useCurrentUserSession = (): {
  currentUserSession: Ref<CommonUseAuthReturn['data'], CommonUseAuthReturn['data']>;
  mainRole: string;
  isInternalUser: boolean;
  currentEnv: { isDemoEnv: boolean; isNextEnv: boolean; isProdEnv: boolean; isSelfhosted: boolean };
  userOrganization: string;
  setCurrentUserSession: (s: { RefreshHandler: RefreshHandler; ModuleOptions: ModuleOptions }) => void;
  isServiceproviderUser: boolean;
  isAdminUser: boolean
} => {
  const { data } = useAuth()
  const currentUserSession = useState('useCurrentUser', () => data)

  const setCurrentUserSession = (s: Ref<CommonUseAuthReturn['data'], CommonUseAuthReturn['data']>): void => {
    currentUserSession.value = s
  }

  const isServiceproviderUser = currentUserSession?.value?.user?.roles?.['x-hasura-allowed-roles'].includes(
    'serviceprovider'
  )

  const isAdminUser = currentUserSession?.value?.user?.roles?.['x-hasura-allowed-roles'].includes(
    'admin'
  )

  const envVars = useRuntimeConfig()
  const isDemoEnv = envVars.public.ENVIRONMENT === 'demo'
  const isNextEnv = envVars.public.ENVIRONMENT === 'next' || envVars.public.ENVIRONMENT === 'dev'
  const isProdEnv = envVars.public.ENVIRONMENT === 'prod'
  const isSelfhosted = envVars.public.ENVIRONMENT === 'selfhosted'

  const currentEnv = {
    isDemoEnv,
    isNextEnv,
    isProdEnv,
    isSelfhosted
  }

  const internalEmailDomains = ['sec-auditor.com', 'thomaskoscheck.de']
  const userEmailDomain = currentUserSession?.value?.user?.email?.split('@')[1] || ''
  const isInternalUser = internalEmailDomains.includes(userEmailDomain)

  // removes all extra roles to return one of the three main roles
  // client, serviceprovider or admin
  const mainRole = getMainRole(currentUserSession?.value?.user?.roles?.['x-hasura-allowed-roles'])
  const userOrganization = currentUserSession?.value?.user?.roles?.['x-hasura-user-org']

  return {
    currentUserSession,
    mainRole,
    isInternalUser,
    currentEnv,
    userOrganization,
    setCurrentUserSession,
    isServiceproviderUser,
    isAdminUser
  }
}
